import React, { useState, useMemo } from 'react';
import { CARDS, cardRarity } from '../game/cards';
import GameCard from './GameCard';
import { Layers, X, Search } from 'lucide-react';

// v2.8 QoL: Deck Preview Sidebar — collapsible searchable deck viewer for shop/reward/etc.
// Usage: <DeckSidebar deck={run.player.deck} />
export default function DeckSidebar({ deck = [] }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('rarity'); // 'rarity' | 'name' | 'cost' | 'type'

  const items = useMemo(() => {
    const arr = deck.map((id, i) => {
      const c = CARDS[id] || {};
      return { id, i, name: c.name || id, cost: typeof c.cost === 'number' ? c.cost : 0, type: c.type || '', rarity: cardRarity(id) };
    });
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      arr.splice(0, arr.length, ...arr.filter(x => x.name.toLowerCase().includes(s) || x.type.toLowerCase().includes(s) || x.rarity.includes(s)));
    }
    if (sort === 'name') arr.sort((a, b) => a.name.localeCompare(b.name));
    if (sort === 'cost') arr.sort((a, b) => a.cost - b.cost);
    if (sort === 'type') arr.sort((a, b) => a.type.localeCompare(b.type));
    if (sort === 'rarity') {
      const ord = { common: 0, uncommon: 1, rare: 2, legendary: 3, divine: 4 };
      arr.sort((a, b) => (ord[b.rarity] || 0) - (ord[a.rarity] || 0));
    }
    return arr;
  }, [deck, q, sort]);

  return (
    <>
      {/* Floating tab (always visible) */}
      <button
        className="fixed left-0 top-1/2 -translate-y-1/2 z-30 brutal-btn text-xs px-2 py-3 flex flex-col items-center gap-1"
        style={{ writingMode: 'vertical-rl', borderColor: 'var(--neon)', color: 'var(--neon)', boxShadow: '4px 0 0 var(--neon)', borderLeft: 0 }}
        onClick={() => setOpen(v => !v)}
        data-testid="deck-sidebar-toggle"
        title="View your full deck"
      >
        <Layers size={14} />
        DECK · {deck.length}
      </button>

      {open && (
        <aside
          className="fixed left-0 top-0 h-full z-40 bg-[#050505] border-r-2 border-[color:var(--neon)] flex flex-col p-3 w-[380px] max-w-full"
          data-testid="deck-sidebar-panel"
          style={{ boxShadow: '8px 0 0 rgba(57,255,20,0.3)' }}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="font-display font-black text-lg uppercase text-[color:var(--neon)] flex items-center gap-2">
              <Layers size={16}/> YOUR DECK · {deck.length}
            </div>
            <button className="brutal-btn text-xs px-2 py-1" onClick={() => setOpen(false)} data-testid="deck-sidebar-close">
              <X size={12}/> CLOSE
            </button>
          </div>
          <div className="flex items-center gap-2 mb-2">
            <div className="flex items-center gap-1 flex-1 border-2 border-[color:var(--muted)] bg-[#0a0a0a] px-2 py-1">
              <Search size={12} className="text-[color:var(--text-dim)]"/>
              <input
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="search deck..."
                className="flex-1 bg-transparent text-xs font-mono uppercase outline-none"
                data-testid="deck-sidebar-search"
              />
            </div>
            <select
              value={sort}
              onChange={e => setSort(e.target.value)}
              className="bg-[#0a0a0a] border-2 border-[color:var(--muted)] text-white font-mono uppercase text-xs px-2 py-1"
              data-testid="deck-sidebar-sort"
            >
              <option value="rarity">rarity</option>
              <option value="name">name</option>
              <option value="cost">cost</option>
              <option value="type">type</option>
            </select>
          </div>
          <div className="flex-1 overflow-y-auto pr-1" data-testid="deck-sidebar-list">
            {items.length === 0 && (
              <div className="text-center text-[color:var(--text-dim)] text-xs uppercase mt-8">— NO MATCHES —</div>
            )}
            <div className="grid grid-cols-2 gap-2">
              {items.map((it) => (
                <div key={`${it.id}-${it.i}`} className="scale-[0.68] origin-top-left" data-testid={`deck-sidebar-card-${it.i}`}>
                  <GameCard cardId={it.id} disabled small={false} testid={`deck-sidebar-inner-${it.i}`} />
                </div>
              ))}
            </div>
          </div>
        </aside>
      )}
    </>
  );
}
